import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { 
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, 
  List, ListOrdered, Quote, Code, Link as LinkIcon, 
  Image as ImageIcon, Heading1, Heading2, Heading3,
  Undo, Redo, RemoveFormatting
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const MenuBar = ({ editor, onImageClick }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
  };

  const buttonClass = (isActive) => 
    `p-2 rounded transition-colors ${isActive ? 'bg-sky-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`;

  return (
    <div className="flex flex-wrap gap-1 p-2 bg-gray-800 border-b border-gray-700 rounded-t-md">
      {/* Text Formatting */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={buttonClass(editor.isActive('bold'))}
        title="Bold"
      >
        <Bold className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={buttonClass(editor.isActive('italic'))}
        title="Italic"
      >
        <Italic className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={buttonClass(editor.isActive('underline'))}
        title="Underline"
      >
        <UnderlineIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={buttonClass(editor.isActive('strike'))}
        title="Strikethrough"
      >
        <Strikethrough className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-gray-600 mx-1 self-center" />

      {/* Headings */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={buttonClass(editor.isActive('heading', { level: 1 }))}
        title="Heading 1"
      >
        <Heading1 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={buttonClass(editor.isActive('heading', { level: 2 }))}
        title="Heading 2"
      >
        <Heading2 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={buttonClass(editor.isActive('heading', { level: 3 }))}
        title="Heading 3"
      >
        <Heading3 className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-gray-600 mx-1 self-center" />

      {/* Lists */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={buttonClass(editor.isActive('bulletList'))}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={buttonClass(editor.isActive('orderedList'))}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-gray-600 mx-1 self-center" />

      {/* Blocks */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={buttonClass(editor.isActive('blockquote'))}
        title="Quote"
      >
        <Quote className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={buttonClass(editor.isActive('codeBlock'))}
        title="Code Block"
      >
        <Code className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-gray-600 mx-1 self-center" />

      {/* Link */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={editor.isActive('link') ? removeLink : addLink}
        className={buttonClass(editor.isActive('link'))}
        title={editor.isActive('link') ? 'Remove Link' : 'Add Link'}
      >
        <LinkIcon className="w-4 h-4" />
      </Button>

      {/* Image */}
      {onImageClick && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onImageClick}
          className={buttonClass(false)}
          title="Insert Image"
        >
          <ImageIcon className="w-4 h-4" />
        </Button>
      )}

      <div className="w-px h-6 bg-gray-600 mx-1 self-center" />

      {/* Undo/Redo */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className={`p-2 rounded transition-colors ${!editor.can().undo() ? 'opacity-50 cursor-not-allowed' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        title="Undo"
      >
        <Undo className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className={`p-2 rounded transition-colors ${!editor.can().redo() ? 'opacity-50 cursor-not-allowed' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        title="Redo"
      >
        <Redo className="w-4 h-4" />
      </Button>

      {/* Clear Formatting */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        className="p-2 rounded bg-gray-700 text-gray-300 hover:bg-gray-600"
        title="Clear Formatting"
      >
        <RemoveFormatting className="w-4 h-4" />
      </Button>
    </div>
  );
};

export const RichTextEditor = ({ 
  content, 
  onChange, 
  placeholder = 'Start writing...', 
  onImageClick,
  className = ''
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-sky-400 underline hover:text-sky-300',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-4',
        },
      }),
      Underline,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[200px] p-4',
      },
    },
  });

  // Update content when prop changes (for edit mode)
  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  // Method to insert image from external source
  const insertImage = (url) => {
    if (editor && url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  // Expose insertImage method
  React.useEffect(() => {
    if (editor) {
      editor.insertImage = insertImage;
    }
  }, [editor]);

  return (
    <div className={`border border-gray-700 rounded-md overflow-hidden ${className}`}>
      <MenuBar editor={editor} onImageClick={onImageClick} />
      <div className="bg-gray-800 text-white">
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .ProseMirror {
          min-height: 200px;
          padding: 1rem;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #6b7280;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror h1 {
          font-size: 1.875rem;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }
        .ProseMirror p {
          margin-bottom: 0.75rem;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .ProseMirror li {
          margin-bottom: 0.25rem;
        }
        .ProseMirror blockquote {
          border-left: 3px solid #0ea5e9;
          padding-left: 1rem;
          margin-left: 0;
          margin-right: 0;
          color: #9ca3af;
          font-style: italic;
        }
        .ProseMirror pre {
          background: #1f2937;
          border-radius: 0.375rem;
          padding: 0.75rem 1rem;
          font-family: monospace;
          overflow-x: auto;
        }
        .ProseMirror code {
          background: #374151;
          border-radius: 0.25rem;
          padding: 0.125rem 0.25rem;
          font-family: monospace;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
